import React, {
  memo,
  useCallback,
  useRef,
  useState,
} from 'react'

import PropTypes from 'prop-types'
import { useConnect } from 'redux-bundler-hook'

import { MoreVert as MoreVertIcon, Person as PersonIcon } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'

import Can from '~/src/Flags/Can'
import createLogger from '~/src/Lib/Logging'
import { idAndNameShape, idPropType } from '~/src/Lib/PropTypes'
import {
  EMPTY_OBJECT,
  getDateTime,
  getShortDateDiff,
  initials,
  useFilestackURL,
} from '~/src/Lib/Utils'
import ConfirmationDialog from '~/src/UI/Shared/ConfirmationDialog'
import MentionField from '~/src/UI/Shared/MentionField'

const displayName = 'CommentWidget'

const logger = createLogger(displayName)

export const styles = theme => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 1, 0.5, 1.5),
    '&:hover': {
      backgroundColor: theme.palette.black.hovered,
      borderRadius: 6,
      '& $commentKabob': {
        visibility: 'visible'
      }
    }
  },
  rootEdit: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 1, 1.5, 2.5),
    borderBottom: `1px solid ${theme.palette.contrast.disabled}`,
    backgroundColor: theme.palette.black.hovered,
    borderRadius: theme.spacing(0.75, 0.75, 0, 0),
    '&:hover': {
      borderColor: theme.palette.contrast.muted
    },
    '&:focus-within': {
      borderColor: theme.palette.blue.main
    },
  },
  textComment: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  avatarIcon: {
    height: 30,
    width: 30,
    marginTop: theme.spacing(1),
    padding: 0,
    fontSize: '1rem'
  },
  personIcon: {
    color: theme.palette.contrastInverse.disabled
  },
  title: {
    color: `${theme.palette.contrast.disabled} !important`,
    fontSize: '0.875rem !important',
    fontWeight: 'normal !important'
  },
  editedLabel: {
    color: `${theme.palette.contrast.disabled} !important`,
    fontSize: '0.875rem !important',
    fontWeight: 'normal !important',
    paddingLeft: theme.spacing(1)
  },
  mentioned: {
    display: 'flex',
    whiteSpace: 'pre-line',
    '& .MuiTypography-body1': {
      color: theme.palette.contrast.strong,
      fontSize: '1rem',
      '& span': {
        fontSize: '1rem',
        fontWeight: 'normal !important'
      },
    },
    '& .aroya__control': {
      fontSize: '1rem',
    },
    '& .aroya__control > textarea': {
      border: 'none',
      borderRadius: 0
    },
  },
  actionButtons: {
    display: 'flex',
    alignSelf: 'flex-end'

  },
  commentKabob: {
    visibility: 'hidden',
    color: theme.palette.primary.main,
    marginTop: theme.spacing(0.5)
  }
})

const useStyles = makeStyles(styles)

const IconAvatar = ({ Icon, membership, person }) => {
  const classes = useStyles()
  const filestackUrl = useFilestackURL()

  return (
    membership?.photo?.handle ? (
      <Avatar
        src={filestackUrl(membership.photo, ['resize=width:30,height:30'])}
        alt={initials(person.fullName)}
        className={classes.avatarIcon}
      />
    ) : (
      <Avatar className={classes.avatarIcon}>
        {Object.values(person).length ? initials(person.fullName) : <Icon className={classes.personIcon} />}
      </Avatar>
    )
  )
}

export const Comment = ({
  classes,
  comment,
  editing,
  editText,
  formattedTitle,
  handleChange,
  Icon,
  membership,
  mentionable,
  person
}) => {
  const { createdOn, id, modifiedOn, value } = comment
  const edited = getDateTime(createdOn).ts !== getDateTime(modifiedOn).ts

  return (
    <Box id={`comment-${comment.id}`} display="flex" width="100%">
      <IconAvatar Icon={Icon} membership={membership} person={person} />
      <div className={classes.mentioned}>
        {editing === id ? (
          <MentionField
            autoFocus
            data-testid="comment-input"
            data={mentionable}
            onChange={handleChange}
            value={editText}
          />
        ) : (
          <Box display="flex" flexDirection="column" pl={1} gap={0.5} width="100%">
            <Typography className={classes.title}>
              {formattedTitle}
              <span className={classes.editedLabel}>{edited ? '(edited)' : null}</span>
            </Typography>
            <MentionField value={value} asText />
          </Box>
        )}
      </div>
    </Box>
  )
}

export const CommentWidgetComponent = ({
  comment = EMPTY_OBJECT,
  currentUserId = null,
  editing = null,
  Icon = PersonIcon,
  onDelete,
  onEdit,
  membership = EMPTY_OBJECT,
  memberships,
  person = EMPTY_OBJECT,
  setEditing,
  users
}) => {
  const classes = useStyles()
  const reqRef = useRef()
  const theme = useTheme()
  const [deleting, setDeleting] = useState(false)
  const [editText, setEditText] = useState('')
  const [kabobEl, setKabobEl] = useState(null)
  const [saving, setSaving] = useState(false)

  const shortDateText = () => {
    const splitDate = getShortDateDiff(comment.createdOn).split(' ')[0]
    if (splitDate.includes('<1m')) return 'Just now'
    if (splitDate.includes('1d')) return 'Yesterday'
    return `${splitDate} ago`
  }

  const commentIsMine = currentUserId === comment.createdBy
  const fullName = person?.fullName

  const formattedTitle = `${fullName ?? ''}${fullName ? ',' : ''} ${shortDateText()}`
  const mentionable = comment.mentions.reduce((allMentions, mention) => {
    const userFullName = users[memberships[mention]?.user]?.fullName ?? ''
    return ([
      ...allMentions,
      { id: mention, display: userFullName }
    ])
  }, [])

  const handleChange = useCallback((e, value, body, mentions) => {
    reqRef.current = { value, mentions, body }
    setEditText(e.target.value)
  }, [reqRef])

  const onKabob = useCallback(e => {
    setKabobEl(e.currentTarget)
  }, [setKabobEl])

  const handleCancelClick = () => {
    setEditText(comment.body)
    setEditing(null)
    setDeleting(false)
  }

  const handleDoneClick = async () => {
    setSaving(true)
    await onEdit({
      id: comment.id,
      note: comment.note,
      value: reqRef.current.value,
      body: reqRef.current.body,
      mentions: reqRef.current.mentions,
    })
    setSaving(false)
    setEditText('')
    setEditing(null)
  }

  const handleEditClick = () => {
    setEditText(comment.value)
    setEditing(comment.id)
    setKabobEl(null)
  }

  const handleDeleteClick = () => {
    setDeleting(true)
    setKabobEl(null)
  }

  const handleConfirmDelete = () => onDelete(comment)

  return (
    <>
      <ConfirmationDialog
        open={deleting}
        title="Delete comment?"
        confirmText="Delete"
        cancelText="Cancel"
        onCancel={handleCancelClick}
        onConfirm={handleConfirmDelete}
      />
      <div
        className={editing === comment.id ? classes.rootEdit : classes.root}
        style={{
          backgroundColor: kabobEl ? theme.palette.black.hovered : null,
          borderRadius: kabobEl ? 6 : null
        }}
      >
        <div className={classes.textComment}>
          <Comment
            classes={classes}
            comment={comment}
            editing={editing}
            editText={editText}
            formattedTitle={formattedTitle}
            handleChange={handleChange}
            Icon={Icon}
            membership={membership}
            mentionable={mentionable}
            person={person}
          />
          {editing === comment.id && (
            <Box className={classes.actionButtons}>
              <Button
                color="primary"
                onClick={handleCancelClick}
                data-testid="comment-cancel"
              >
                Cancel
              </Button>
              <Button
                color="primary"
                onClick={handleDoneClick}
                disabled={editText === comment?.value || saving}
                data-testid="comment-done"
              >
                Done
              </Button>
            </Box>
          )}
        </div>

        {editing === comment.id ? null : (
          <Can any actions={[() => commentIsMine]}>
            <IconButton
              className={classes.commentKabob}
              onClick={onKabob}
              size="small"
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={kabobEl}
              open={Boolean(kabobEl)}
              onClose={() => setKabobEl(null)}
            >
              <MenuItem onClick={handleEditClick}>
                <Typography color="primary" style={{ fontWeight: 600 }}>
                  Edit
                </Typography>
              </MenuItem>

              <MenuItem onClick={handleDeleteClick}>
                <Typography color="error" style={{ fontWeight: 600 }}>
                  Delete
                </Typography>
              </MenuItem>

            </Menu>
          </Can>
        )}
      </div>
    </>
  )
}

CommentWidgetComponent.displayName = displayName
CommentWidgetComponent.propTypes = {
  comment: PropTypes.shape({
    body: PropTypes.string,
    createdBy: idPropType,
    createdOn: PropTypes.string,
    id: idPropType,
    mentions: PropTypes.arrayOf(idPropType),
    note: idPropType,
    value: PropTypes.string
  }),
  currentUserId: idPropType,
  editing: idPropType.nullable,
  Icon: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  membership: PropTypes.shape({
    photo: PropTypes.shape({
      handle: PropTypes.string
    })
  }),
  memberships: idAndNameShape.isRequired,
  person: PropTypes.shape({
    id: idPropType,
    fullName: PropTypes.string
  }),
  setEditing: PropTypes.func.isRequired,
  users: idAndNameShape.isRequired
}

const CommentWidget = ({
  comment = EMPTY_OBJECT,
  ...restProps
}) => {
  const {
    currentMembership,
    facilityMemberships = EMPTY_OBJECT,
    users = EMPTY_OBJECT,
  } = useConnect(
    'selectCurrentMembership',
    'selectFacilityMemberships',
    'selectMe',
    'selectUsers',
  )

  const membershipId = Object.values(facilityMemberships).find(m => m.user === comment.createdBy)?.id
  const { [membershipId]: membership } = facilityMemberships
  const { [comment.createdBy]: user } = users

  if (!comment) return null

  return (
    <CommentWidgetComponent
      {...restProps}
      comment={comment}
      currentUserId={currentMembership.user}
      membership={membership}
      memberships={facilityMemberships}
      person={user}
      users={users}
    />
  )
}

CommentWidget.displayName = `Connected(${CommentWidgetComponent.displayName})`
CommentWidget.propTypes = {
  comment: PropTypes.shape({
    id: idPropType
  }).isRequired,
  Icon: PropTypes.element
}

const MemoizedComment = memo(CommentWidget)

export default MemoizedComment
